import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CalendarDays, Car, Clock, Coins, Landmark, Lightbulb, Map as MapIcon, Minus, Plus, Search, Trash2, Users, Gauge } from "lucide-react";
import { API, api } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { MapPanel } from "@/components/builder/MapPanel";
import { JourneyPreview } from "@/components/builder/JourneyPreview";
import { BespokeDialog } from "@/components/builder/BespokeDialog";
import { GmapsProvider, GMAPS_KEY } from "@/components/builder/gmaps";
import { PlaceSearch } from "@/components/builder/PlaceSearch";
import { BudgetInput, BuilderHero, CatalogDrawer, ModeCards, PrefCell, TransportCapacityNotice, TransportPicker, transportCap } from "@/components/builder/BuilderChrome";
import { CityExplorer } from "@/components/builder/CityExplorer";
import { ExperienceCatalog } from "@/components/builder/ExperienceCatalog";
import { fmtDrive, fmtKm, useRouteLegs } from "@/components/builder/useRouteLegs";
import { EstimateRow, useEstimate } from "@/components/builder/Estimate";

const DRAFT_KEY = "tv_builder_rome";
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || null; } catch { return null; } };

const fmtMin = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ""}` : `${min}m`);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const attrPhoto = (a) => (a.kind === "experience" ? a.image : a.has_photo ? `${API}/public/cities/roma/attractions/${a.id}/photo` : a.image || null);

export default function RomeBuilder() {
    return (
        <GmapsProvider>
            <RomeBuilderInner />
        </GmapsProvider>
    );
}

function RomeBuilderInner() {
    const { lang, t } = useLang();
    const [places, setPlaces] = useState([]);
    const [romaMeta, setRomaMeta] = useState(null);
    const [stops, setStops] = useState(() => loadDraft()?.stops || []);
    const [prefs, setPrefs] = useState(() => {
        const d = loadDraft()?.prefs || {};
        return { date: d.date || "", days: d.days || 2, participants: d.participants || 2, pace: d.pace || "balanced", interests: d.interests || [], transport: d.transport || "car", transportCustom: d.transportCustom || false, budget: d.budget || "", notes: d.notes || "" };
    });
    const [q, setQ] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [activeId, setActiveId] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [bespokeOpen, setBespokeOpen] = useState(false);
    const [explorerOpen, setExplorerOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expCat, setExpCat] = useState(null);

    useEffect(() => {
        api.get("/public/cities/roma/content").then((r) => setPlaces(r.data.attractions || [])).catch(() => {});
        api.get("/public/cities").then((r) => setRomaMeta((r.data.cities || []).find((c) => c.id === "roma") || null)).catch(() => {});
    }, []);

    /* ?categoria=<k> dalla homepage: apre il catalogo esperienze (solo Roma e dintorni). */
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const cat = searchParams.get("categoria");
        if (cat && t.builder.interests[cat]) {
            setPrefs((p) => ({ ...p, interests: p.interests.includes(cat) ? p.interests : [...p.interests, cat] }));
            setExpCat(cat);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ stops, prefs }));
    }, [stops, prefs]);

    const days = Math.min(30, Math.max(1, Number(prefs.days) || 1));
    const inStops = (id) => stops.some((s) => s.id === id);
    const leastLoadedDay = (list) => {
        const load = Array.from({ length: days }, () => 0);
        list.forEach((s) => { if (s.day >= 1 && s.day <= days) load[s.day - 1] += s.duration_min || 60; });
        return load.indexOf(Math.min(...load)) + 1;
    };
    const addStop = (p) => setStops((s) => [...s, { id: p.id, name: pick(p.name, lang), lat: p.lat, lng: p.lng, duration_min: p.duration_min, has_photo: p.has_photo, image: p.image, day: leastLoadedDay(s) }]);
    const addCustomStop = (p) => setStops((s) => [...s, { id: p.id, name: p.name, lat: p.lat, lng: p.lng, duration_min: 60, has_photo: false, image: null, custom: true, day: leastLoadedDay(s) }]);
    const removeStop = (id) => setStops((s) => s.filter((x) => x.id !== id));
    const move = (i, dir) => setStops((s) => { const a = [...s]; const j = i + dir; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });
    const setDay = (id, day) => setStops((s) => s.map((x) => (x.id === id ? { ...x, day } : x)));

    const route = useRouteLegs(stops);

    const cats = useMemo(() => [...new Set(places.flatMap((p) => p.cats || []))], [places]);
    const filtered = useMemo(() => places.filter((p) => {
        const text = (pick(p.name, lang) + " " + pick(p.desc, lang)).toLowerCase();
        return (!q || text.includes(q.toLowerCase())) && (!catFilter || (p.cats || []).includes(catFilter));
    }), [places, q, catFilter, lang]);

    const totalMin = stops.reduce((a, s) => a + (s.duration_min || 0), 0);
    const estimate = useEstimate("rome", {
        stops: stops.length,
        km: route ? route.totalDistanceM / 1000 : 0,
        participants: parseInt(prefs.participants, 10) || 1,
    });

    const suggestions = useMemo(() => {
        const out = [];
        const firstMust = places.find((p) => p.must_see && !inStops(p.id));
        if (firstMust) out.push({ text: t.builder.suggNearby.replace("{name}", pick(firstMust.name, lang)), addId: firstMust.id });
        if (prefs.pace === "relaxed" && totalMin > 300) out.push({ text: t.builder.suggRelaxed.replace("{n}", stops.length), warn: true });
        return out.slice(0, 2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stops, places, prefs.pace, totalMin, lang]);

    const toggleInterest = (k) => {
        setPrefs((p) => ({ ...p, interests: p.interests.includes(k) ? p.interests.filter((x) => x !== k) : [...p.interests, k] }));
        setExpCat((c) => (c === k ? null : k));
    };
    const addExperience = (e) => setStops((s) => (s.some((x) => x.id === e.id) ? s : [...s, {
        id: e.id, name: pick(e.name, lang), lat: e.lat, lng: e.lng,
        duration_min: e.duration_min, has_photo: false,
        image: e.has_photo ? `${API}/public/experiences/${e.id}/photo` : null,
        kind: "experience", category: e.category, day: leastLoadedDay(s),
    }]));

    return (
        <div data-testid="rome-builder" className="marble-bg min-h-screen pt-24 pb-24">
            <Seo lang={lang} title={`${t.builder.romeCard} — TOURVOYAGERS`} description={t.builder.romeSub} />
            <div className="max-w-[90rem] mx-auto px-5 sm:px-8">
                <Link to="/itinerario" data-testid="builder-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-400 hover:text-amber-300 transition-colors">
                    <ArrowLeft size={14} /> {t.builder.changeMode}
                </Link>

                <Reveal><div className="mt-5"><BuilderHero title={t.builder.heroRomeTitle} sub={t.builder.heroRomeSub} /></div></Reveal>
                <Reveal delay={0.08}><div className="mt-5"><ModeCards active="roma" /></div></Reveal>

                {/* BARRA PREFERENZE */}
                <Reveal delay={0.14}>
                    <div className="mt-5 rounded-2xl border border-amber-500/20 bg-[#0b0b0c]/85" data-testid="rome-preferences">
                        <div className="grid sm:grid-cols-2 xl:grid-cols-4 sm:divide-x sm:divide-amber-500/10">
                            <PrefCell icon={CalendarDays} label={t.builder.prefDate} testId="prefcell-date">
                                <input data-testid="pref-date" type="date" className="input-lux !py-2 [color-scheme:dark]" value={prefs.date} onChange={(e) => setPrefs({ ...prefs, date: e.target.value })} />
                            </PrefCell>
                            <PrefCell icon={Clock} label={t.builder.romeStayDays} testId="prefcell-days">
                                <div className="flex items-center gap-3">
                                    <button data-testid="pref-days-minus" onClick={() => setPrefs({ ...prefs, days: Math.max(1, days - 1) })} className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-400 hover:text-black transition-colors"><Minus size={12} /></button>
                                    <span className="w-6 text-center font-mono-accent text-base text-amber-100" data-testid="pref-days">{days}</span>
                                    <button data-testid="pref-days-plus" onClick={() => setPrefs({ ...prefs, days: Math.min(30, days + 1) })} className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-400 hover:text-black transition-colors"><Plus size={12} /></button>
                                </div>
                            </PrefCell>
                            <PrefCell icon={Users} label={t.builder.prefPax} testId="prefcell-pax">
                                <input data-testid="pref-pax" type="number" min="1" max="30" className="input-lux !py-2" value={prefs.participants} onChange={(e) => setPrefs({ ...prefs, participants: e.target.value })} />
                            </PrefCell>
                            <PrefCell icon={Gauge} label={t.builder.prefPace} testId="prefcell-pace">
                                <select data-testid="pref-pace" className="input-lux !py-2" value={prefs.pace} onChange={(e) => setPrefs({ ...prefs, pace: e.target.value })}>
                                    {Object.entries(t.builder.paces).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            </PrefCell>
                            <PrefCell icon={Car} label={t.builder.transport} testId="prefcell-transport">
                                <TransportPicker value={prefs.transport} onChange={(v) => setPrefs({ ...prefs, transport: v })} />
                            </PrefCell>
                            <PrefCell icon={Coins} label={t.builder.budget} testId="prefcell-budget">
                                <BudgetInput value={prefs.budget} onChange={(v) => setPrefs({ ...prefs, budget: v })} />
                            </PrefCell>
                        </div>
                        <TransportCapacityNotice transport={prefs.transport} participants={prefs.participants}
                            custom={prefs.transportCustom} onCustom={() => setPrefs({ ...prefs, transportCustom: !prefs.transportCustom })} />
                        <div className="flex flex-wrap gap-2 border-t border-amber-500/10 px-5 py-4">
                            {Object.entries(t.builder.interests).map(([k, v]) => (
                                <button key={k} type="button" data-testid={`interest-${k}`} onClick={() => toggleInterest(k)}
                                    className={`rounded-full border px-4 py-1.5 text-[11px] font-semibold transition-colors ${expCat === k ? "border-amber-400 bg-amber-400 text-black" : prefs.interests.includes(k) ? "border-amber-400/70 bg-amber-400/15 text-amber-200" : "border-amber-500/25 text-stone-300 hover:border-amber-400/60"}`}>
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </Reveal>

                {/* CATALOGO ESPERIENZE (solo Roma e dintorni in questa modalità) */}
                {expCat && (
                    <ExperienceCatalog category={expCat} mode="roma"
                        addedIds={stops.filter((s) => s.kind === "experience").map((s) => s.id)}
                        onAdd={addExperience} onRemove={removeStop} onClose={() => setExpCat(null)} />
                )}

                <div className="mt-12 grid gap-10 xl:grid-cols-12">
                    {/* COLONNA PRINCIPALE — IL TUO SOGGIORNO ROMANO */}
                    <div className="min-w-0 xl:col-span-7">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div>
                                <h2 className="flex items-center gap-2.5 font-display text-2xl sm:text-3xl text-amber-50">
                                    <Landmark size={20} className="text-amber-400" /> {t.builder.yourRomeTitle}
                                </h2>
                                <p className="mt-1 text-xs text-stone-500">
                                    {t.builder.yourRomeSub}
                                    {prefs.date && <span className="ml-2 inline-flex items-center gap-1 text-stone-400"><CalendarDays size={11} className="text-amber-500/80" />{prefs.date} → {addDays(prefs.date, days)}</span>}
                                </p>
                            </div>
                            <button data-testid="add-place" onClick={() => setDrawerOpen(true)}
                                className="inline-flex items-center gap-2 rounded-full border border-amber-500/50 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400/10">
                                <Plus size={14} /> {t.builder.addPlace}
                            </button>
                        </div>

                        <div className="mt-6 space-y-4" data-testid="rome-stops">
                            {stops.length === 0 && (
                                <button data-testid="stops-empty" onClick={() => setDrawerOpen(true)}
                                    className="gold-card flex w-full flex-col items-center gap-3 rounded-2xl border-dashed p-12 text-center transition-colors hover:border-amber-400/50">
                                    <Plus size={26} className="text-amber-400" />
                                    <span className="text-sm text-stone-400">{t.builder.stopsEmpty}</span>
                                </button>
                            )}
                            {stops.map((s, i) => (
                                <Fragment key={s.id}>
                                    {(i === 0 || stops[i - 1].day !== s.day) && (
                                        <p className="pt-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-amber-500/80" data-testid={`day-header-${s.day || 0}`}>
                                            {s.day ? t.builder.dayN.replace("{n}", s.day) : t.builder.dayUnassigned}{prefs.date && s.day ? ` · ${addDays(prefs.date, s.day - 1)}` : ""}
                                        </p>
                                    )}
                                    <Reveal>
                                    <div data-testid={`stop-item-${s.id}`} onClick={() => setActiveId(s.id)}
                                        className={`gold-card flex items-center gap-4 rounded-2xl p-3.5 cursor-pointer transition-colors ${activeId === s.id ? "!border-amber-400/70" : ""}`}>
                                        <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-xl">
                                            <GoldImage src={attrPhoto(s)} alt={s.name} className="absolute inset-0 h-full w-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="truncate font-display text-lg text-amber-50">{s.name}</p>
                                            <p className="mt-0.5 text-[11px] text-stone-500">{s.kind === "experience" ? `${t.builder.expBadge} · ` : ""}~{fmtMin(s.duration_min)}{s.custom ? ` · ${t.builder.customPlace}` : ""}</p>
                                        </div>
                                        <select aria-label={t.builder.dayAssign} value={s.day || ""} onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => setDay(s.id, Number(e.target.value))}
                                            data-testid={`stop-day-${s.id}`}
                                            className="cursor-pointer rounded-lg border border-amber-500/25 bg-[#0b0b0c] px-2 py-1.5 text-[11px] text-amber-300 focus:outline-none">
                                            {Array.from({ length: days }, (_, x) => x + 1).map((n) => <option key={n} value={n}>{t.builder.dayN.replace("{n}", n)}</option>)}
                                        </select>
                                        <div className="flex flex-col gap-0.5">
                                            <button data-testid={`stop-up-${s.id}`} onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="text-stone-500 hover:text-amber-300 disabled:opacity-30" disabled={i === 0}><ArrowUp size={13} /></button>
                                            <button data-testid={`stop-down-${s.id}`} onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="text-stone-500 hover:text-amber-300 disabled:opacity-30" disabled={i === stops.length - 1}><ArrowDown size={13} /></button>
                                        </div>
                                        <button data-testid={`stop-remove-${s.id}`} onClick={(e) => { e.stopPropagation(); removeStop(s.id); }} className="text-stone-500 hover:text-red-400"><Trash2 size={14} /></button>
                                    </div>
                                    </Reveal>
                                </Fragment>
                            ))}
                        </div>

                        {suggestions.length > 0 && (
                            <div className="mt-5 space-y-2" data-testid="rome-suggestions">
                                {suggestions.map((s, i) => (
                                    <div key={i} className={`flex items-center gap-2.5 rounded-lg border p-3 text-xs ${s.warn ? "border-amber-600/40 bg-amber-400/5 text-amber-200" : "border-amber-500/20 bg-[#101011] text-stone-300"}`}>
                                        <Lightbulb size={14} className="shrink-0 text-amber-400" />
                                        <span className="flex-1">{s.text}</span>
                                        {s.addId && (
                                            <button data-testid={`suggestion-add-${s.addId}`} onClick={() => { const p = places.find((x) => x.id === s.addId); if (p) addStop(p); }} className="shrink-0 rounded-full bg-amber-400/15 border border-amber-500/40 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300 hover:bg-amber-400 hover:text-black transition-colors">
                                                {t.builder.add}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* BARRA AZIONI FINALE */}
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                            <button data-testid="explore-roma" onClick={() => setExplorerOpen(true)}
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-500/50 px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-200 transition-colors hover:bg-amber-400/10">
                                <Landmark size={14} /> {t.builder.exploreCity} — Roma
                            </button>
                            <div className="flex-1" />
                            <button data-testid="continue-summary" onClick={() => setPreviewOpen(true)} disabled={stops.length === 0}
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_28px_rgba(241,211,108,0.4)] disabled:opacity-40">
                                {t.builder.continueSummary} <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>

                    {/* COLONNA DESTRA — MAPPA + RIEPILOGO */}
                    <div className="min-w-0 xl:col-span-5">
                        <MapPanel stops={stops} activeId={activeId} onMarkerClick={setActiveId} height="420px" routePath={route?.path} />
                        <p className="mt-2 text-[10px] text-stone-600">{t.builder.mapIndicative}</p>
                        <div className="mt-5 gold-card rounded-2xl p-6" data-testid="rome-summary">
                            <h3 className="font-display text-xl text-amber-100">{t.builder.sumTitle}</h3>
                            <div className="mt-4 space-y-3 text-sm">
                                <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                    <span className="text-stone-400">{t.builder.sumStops}</span>
                                    <span className="text-amber-100 font-semibold" data-testid="summary-stops">{stops.length}</span>
                                </div>
                                <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                    <span className="text-stone-400">{t.builder.romeStayDays}</span>
                                    <span className="text-amber-100 font-semibold" data-testid="summary-days">{days}</span>
                                </div>
                                <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                    <span className="text-stone-400">{t.builder.sumDuration}</span>
                                    <span className="text-amber-100 font-semibold" data-testid="summary-duration">~{fmtMin(totalMin)}</span>
                                </div>
                                <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                    <span className="text-stone-400">{t.builder.transport}</span>
                                    <span className="text-amber-100 font-semibold" data-testid="summary-transport">{t.builder.transports[prefs.transport]}{prefs.transportCustom ? ` · ${t.builder.capCustomBtn}` : ` · ${t.builder.transportCap.replace("{n}", transportCap(prefs.transport))}`}</span>
                                </div>
                                {prefs.budget && (
                                    <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                        <span className="text-stone-400">{t.builder.sumBudget}</span>
                                        <span className="text-amber-100 font-semibold" data-testid="summary-budget">€ {Number(prefs.budget).toLocaleString("it-IT")}</span>
                                    </div>
                                )}
                                {route && (
                                    <>
                                        <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                            <span className="text-stone-400">{t.builder.sumDistance}</span>
                                            <span className="text-amber-100 font-semibold" data-testid="summary-distance">{fmtKm(route.totalDistanceM)}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                            <span className="text-stone-400">{t.builder.sumDrive}</span>
                                            <span className="text-amber-100 font-semibold" data-testid="summary-drive">{fmtDrive(route.totalDurationS)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <EstimateRow estimate={estimate} />
                            <p className="mt-3 text-[10px] leading-relaxed text-stone-600">{estimate ? t.builder.estNote : t.builder.partialNote}</p>
                            <button data-testid="open-bespoke" onClick={() => setBespokeOpen(true)} disabled={stops.length === 0}
                                className="mt-5 w-full rounded-full bg-amber-400 px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.14em] text-black hover:bg-amber-300 transition-all disabled:opacity-40">
                                {t.builder.requestQuote}
                            </button>
                            <button data-testid="open-preview" onClick={() => setPreviewOpen(true)} disabled={stops.length === 0}
                                className="mt-3 w-full rounded-full border border-amber-500/50 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-200 hover:bg-amber-400/10 transition-colors disabled:opacity-40">
                                {t.builder.preview}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* DRAWER AGGIUNGI LUOGO */}
            <CatalogDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={t.builder.catalogTitle}>
                {GMAPS_KEY && (
                    <PlaceSearch testId="place-google-search" bias={{ lat: 41.9028, lng: 12.4964 }} onPick={(p) => { if (!inStops(p.id)) addCustomStop(p); }} />
                )}
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-500/20 bg-[#0b0b0c] px-3 py-2.5">
                    <Search size={15} className="text-amber-500/70 shrink-0" />
                    <input data-testid="place-search" className="w-full bg-transparent text-sm text-stone-200 placeholder:text-stone-500 focus:outline-none" placeholder={t.builder.searchPh} value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {cats.map((c) => (
                        <button key={c} data-testid={`cat-filter-${c}`} onClick={() => setCatFilter(catFilter === c ? "" : c)}
                            className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors ${catFilter === c ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/25 text-stone-400 hover:border-amber-400/60"}`}>
                            {t.builder.interests[c] || c}
                        </button>
                    ))}
                </div>
                <div className="mt-4 space-y-2.5">
                    {filtered.map((p) => (
                        <div key={p.id} data-testid={`place-card-${p.id}`} className="gold-card flex items-center gap-3 rounded-xl p-2.5">
                            <GoldImage src={attrPhoto(p)} alt={pick(p.name, lang)} className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                            <div className="flex-1 min-w-0">
                                <p className="truncate text-sm font-medium text-amber-50">{pick(p.name, lang)}</p>
                                <p className="text-[10px] text-stone-500">~{fmtMin(p.duration_min)}{p.must_see ? " · ★" : ""}</p>
                            </div>
                            {inStops(p.id) ? (
                                <span className="shrink-0 text-xs font-bold text-emerald-300">✓</span>
                            ) : (
                                <button data-testid={`place-add-${p.id}`} onClick={() => addStop(p)} aria-label={t.builder.add}
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-500/40 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                                    <Plus size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </CatalogDrawer>

            <JourneyPreview open={previewOpen} onClose={() => setPreviewOpen(false)} mode="rome" stops={stops} legs={[]} prefs={prefs} totalMin={totalMin} route={route} estimate={estimate} onRequest={() => { setPreviewOpen(false); setBespokeOpen(true); }} />
            <BespokeDialog open={bespokeOpen} onClose={() => setBespokeOpen(false)} mode="rome" stops={stops} prefs={prefs} />
            <CityExplorer
                open={explorerOpen}
                onClose={() => setExplorerOpen(false)}
                cityId="roma"
                cityMeta={romaMeta}
                canAdd
                addedIds={stops.map((s) => s.id)}
                onAdd={(a) => { if (!inStops(a.id)) addStop(a); }}
                onRemove={removeStop}
            />
        </div>
    );
}
